import { ActionIcon, Badge, Box, Group, MultiSelect, Select, Text, TextInput, Tooltip } from '@mantine/core'
import { DatePicker } from '@mantine/dates'
import { IconEye, IconQuestionMark, IconThumbDown, IconThumbUp } from '@tabler/icons-react'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import type { MRT_ColumnDef } from 'mantine-react-table'

dayjs.extend(utcPlugin)
import { useMemo } from 'react'

import type { FeedbackAnnotation, FeedbackTag, FeedbackType } from '@/services/api/api.types'
import { createContentModuleFilterOptions, createTopicFilterOptions } from '@/utils/contentModuleUtils'

import { CALENDAR_STYLES, FILTER_STYLES } from './filterStyles'

interface CreateColumnsParams {
  topicsData?: Array<{ id: string; displayName: string }>
  contentModulesData?: Record<string, number> // byContentModule object with "title_id" -> count mapping
  byTopicData?: Record<string, number> // byTopic stats object with topic ID -> count mapping
  tagsData?: FeedbackTag[]
  onViewDetails: (feedback: FeedbackAnnotation) => void
  topicsEnabled?: boolean
}

const TOPIC_COLUMN_ID = 'cmTopics'

/** What the Mantine 8 range DatePicker writes into column filter state: "YYYY-MM-DD". */
export type DateRangeFilter = [string | null, string | null]

export function createFeedbackTableColumns({
  topicsData = [],
  contentModulesData = {},
  byTopicData = {},
  tagsData,
  onViewDetails,
  topicsEnabled = false,
}: CreateColumnsParams): MRT_ColumnDef<FeedbackAnnotation>[] {
  const columns: MRT_ColumnDef<FeedbackAnnotation>[] = [
    // 1. Date
    {
      header: 'Date',
      accessorKey: 'createdAt',
      enableSorting: true,
      sortDescFirst: true,
      enableColumnFilter: true,
      grow: false,
      size: 100,
      Filter: ({ column }) => {
        const filterValue = column.getFilterValue() as DateRangeFilter | undefined

        return (
          <DatePicker
            type="range"
            value={filterValue || [null, null]}
            onChange={value => {
              // Only set filter if we have at least one date selected
              if (value && (value[0] || value[1])) {
                column.setFilterValue(value)
              } else {
                column.setFilterValue(undefined)
              }
            }}
            allowSingleDateInRange
            size="xs"
            styles={CALENDAR_STYLES}
          />
        )
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<string>()
        return <Text size="sm">{dayjs.utc(value).local().format('MMM D, YYYY')}</Text>
      },
    },
    // 2. Topic (from cmTopics array) - filtered out when topics are disabled
    {
      id: TOPIC_COLUMN_ID,
      header: 'Topic',
      accessorKey: 'cmTopics',
      grow: false,
      size: 170,
      enableSorting: false, // Disabling for now since backend cannot sort by title at the moment (only uuid)
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true
        const cmTopics = row.getValue(columnId) as string[]
        if (!cmTopics || cmTopics.length === 0) return false
        return (filterValue as string[]).some(selectedTopicId => cmTopics.includes(selectedTopicId))
      },
      Filter: ({ column }) => {
        const topicOptions = createTopicFilterOptions(byTopicData, topicsData)

        return (
          <MultiSelect
            data={topicOptions}
            placeholder="Filter by topics"
            value={(column.getFilterValue() as string[]) || []}
            onChange={value => column.setFilterValue(value.length > 0 ? value : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        )
      },
      Cell: ({ row }) => {
        const cmTopics = row.original.cmTopics
        const topicName = row.original.topicName

        // Drop topic IDs not in the sponsor list (e.g. help modules) so UUIDs aren't shown
        const topicsToDisplay =
          cmTopics && cmTopics.length > 0
            ? cmTopics
                .map(topicId => topicsData.find(t => t.id === topicId)?.displayName)
                .filter((displayName): displayName is string => Boolean(displayName))
            : topicName
              ? [topicName]
              : []

        return topicsToDisplay.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {topicsToDisplay.map((topic, index) => (
              <Text
                key={index}
                size="sm"
                style={{
                  backgroundColor: 'var(--mantine-color-blue-1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  color: 'var(--mantine-color-blue-7)',
                  display: 'inline-block',
                }}>
                {topic}
              </Text>
            ))}
          </Group>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No topic
          </Text>
        )
      },
    },
    // 3. Module
    {
      header: 'Module',
      accessorKey: 'contentModuleId',
      grow: true,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true
        const contentModuleId = row.getValue(columnId) as string
        if (!contentModuleId) return false
        // Check if the row's content module ID matches any of the selected filter IDs
        return filterValue.includes(contentModuleId)
      },
      Filter: ({ column }) => {
        const contentModuleOptions = createContentModuleFilterOptions(contentModulesData)

        return (
          <MultiSelect
            data={contentModuleOptions}
            placeholder="Filter by modules"
            value={(column.getFilterValue() as string[]) || []}
            onChange={value => column.setFilterValue(value.length > 0 ? value : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        )
      },
      Cell: ({ row }) => {
        const contentModuleTitle = row.original.contentModuleTitle
        return contentModuleTitle ? (
          <Tooltip label={contentModuleTitle} position="top-start" withArrow>
            <Text size="sm" lineClamp={2}>
              {contentModuleTitle}
            </Text>
          </Tooltip>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No module
          </Text>
        )
      },
    },
    // 4. Rating (positive/negative)
    {
      header: 'Rating',
      accessorKey: 'annotationKind',
      grow: false,
      size: 80,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'equals',
      Filter: ({ column }) => {
        const feedbackOptions = [
          { value: 'thumbs-up', label: 'Positive' },
          { value: 'thumbs-down', label: 'Negative' },
          { value: 'none', label: 'None' },
        ]

        return (
          <Select
            data={feedbackOptions}
            placeholder="Filter by rating"
            value={(column.getFilterValue() as string) || ''}
            onChange={value => column.setFilterValue(value || undefined)}
            clearable
            size="sm"
            styles={FILTER_STYLES}
          />
        )
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<FeedbackType>()
        const isPositive = value === 'thumbs-up'
        const isNegative = value === 'thumbs-down'

        let icon, label
        if (isPositive) {
          icon = <IconThumbUp size={20} color="green" />
          label = 'Positive'
        } else if (isNegative) {
          icon = <IconThumbDown size={20} color="red" />
          label = 'Negative'
        } else {
          icon = <IconQuestionMark size={20} color="gray" />
          label = 'Unknown'
        }

        return (
          <Group justify="center">
            <Tooltip label={label} position="top" withArrow>
              {icon}
            </Tooltip>
          </Group>
        )
      },
    },
    // 5. Tag
    {
      header: 'Tag',
      accessorKey: 'tagIds',
      grow: false,
      size: 120,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true
        const tagIds = row.getValue(columnId) as Array<{ tagId: string; tagName: string }> | null
        if (!tagIds || tagIds.length === 0) return false
        return (filterValue as string[]).some(selectedTagId => tagIds.some(t => t.tagId === selectedTagId))
      },
      Filter: ({ column }) => {
        const uniqueTags = (tagsData || []).filter((tag, index, self) => {
          const isFirst = index === self.findIndex(t => t.id === tag.id)
          if (!isFirst) {
            console.warn(`[FeedbackTable] Duplicate tag id detected: "${tag.id}"`)
          }
          return isFirst
        })
        const tagOptions = uniqueTags.map(tag => ({
          value: tag.id,
          label: tag.name,
        }))

        return (
          <MultiSelect
            data={tagOptions}
            placeholder="Filter by tags"
            value={(column.getFilterValue() as string[]) || []}
            onChange={value => column.setFilterValue(value.length > 0 ? value : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        )
      },
      Cell: ({ row }) => {
        const tags = row.original.tagIds
        return tags && tags.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {tags.map((tag, index) => (
              <Badge key={tag.tagId || index} variant="light" size="sm">
                {tag.tagName}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No tag
          </Text>
        )
      },
    },
    // 6. Comment (user feedback)
    {
      header: 'Comment',
      accessorKey: 'userFeedback',
      grow: true,
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: 'includesString',
      Filter: ({ column }) => {
        return (
          <TextInput
            placeholder="Search comments..."
            value={(column.getFilterValue() as string) || ''}
            onChange={event => {
              const value = event.currentTarget.value
              column.setFilterValue(value || undefined)
            }}
            size="sm"
            styles={{ input: FILTER_STYLES.input }}
          />
        )
      },
      Cell: ({ cell }) => {
        const value = cell.getValue<string | null>()
        return value ? (
          <Tooltip label={value} position="top-start" withArrow>
            <Text size="sm" lineClamp={2}>
              {value}
            </Text>
          </Tooltip>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No comment
          </Text>
        )
      },
    },
    // 7. Detail (eye icon)
    {
      header: 'Detail',
      grow: false,
      size: 50,
      enableSorting: false,
      Cell: ({ row }) => {
        return (
          <Box style={{ textAlign: 'center' }}>
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={() => onViewDetails(row.original)}
              size="sm"
              aria-label="View feedback details">
              <IconEye size={16} aria-hidden="true" />
            </ActionIcon>
          </Box>
        )
      },
    },
  ]

  // Filter out topic column when topics are disabled
  if (!topicsEnabled) {
    return columns.filter(col => col.id !== TOPIC_COLUMN_ID)
  }

  return columns
}

export function useFeedbackTableColumns(params: CreateColumnsParams) {
  return useMemo(
    () => createFeedbackTableColumns(params),
    [params.topicsData, params.contentModulesData, params.tagsData, params.onViewDetails, params.topicsEnabled],
  )
}
